import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, Calendar, Search, ChevronDown, ChevronUp, ArrowLeft, Mail, MessageSquare, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fmtDate } from '@/lib/utils';
import { EXPERIENCE_DATA, RIG_DATA } from '@/components/booking/bookingDefaults';
import WaitlistAdmin from '@/components/booking/WaitlistAdmin';
import BookingDisclaimerPanel from '@/components/admin/BookingDisclaimerPanel';
import BookingEmailComposer from '@/components/admin/BookingEmailComposer';
import BookingSmsComposer from '@/components/admin/BookingSmsComposer';
import CaseSupportLink from '@/components/admin/cases/CaseSupportLink';
import SystemUpdateReschedulePanel from '@/components/admin/SystemUpdateReschedulePanel';
import BookingsKpiStrip from '@/components/admin/BookingsKpiStrip';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/lib/AuthContext';

const STATUSES = ['pending', 'confirmed', 'completed', 'cancelled'];

const STATUS_STYLES = {
  pending: 'bg-yellow-400/10 text-yellow-400',
  confirmed: 'bg-primary/10 text-primary',
  completed: 'bg-green-400/10 text-green-400',
  cancelled: 'bg-red-400/10 text-red-400',
};

function experienceName(key) {
  return EXPERIENCE_DATA[key]?.name || (key ? key.replace(/_/g, ' ').toUpperCase() : '—');
}
function rigName(key) {
  return RIG_DATA[key]?.name || (key ? key.replace(/_/g, ' ').toUpperCase() : '—');
}
function isWalkIn(b) {
  return (b.notes || '').includes('Walk-in (iPad kiosk)');
}

const REFERRAL_LABELS = {
  tripadvisor: 'TripAdvisor',
  google: 'Google',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  word_of_mouth: 'Friend / Family',
  returning_customer: 'Returning customer',
  other: 'Other',
};
function referralLabel(key) {
  return key ? (REFERRAL_LABELS[key] || key) : '';
}

function BookingRow({ booking, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showSms, setShowSms] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const { toast } = useToast();
  const status = booking.status || 'pending';

  const updateStatus = async (newStatus) => {
    if (newStatus === status) return;
    setSaving(true);
    try {
      if (newStatus === 'completed') {
        const res = await base44.functions.invoke('completeBooking', { bookingId: booking.id });
        if (res.data?.error) throw new Error(res.data.error);
        toast({ title: 'Booking completed', description: 'Feedback survey email sent to the customer.' });
      } else {
        await base44.entities.Booking.update(booking.id, { status: newStatus });
      }
      onStatusChange();
    } catch (e) {
      toast({ title: 'Could not update status', description: e.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#111115] border border-white/5">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between p-4 text-left">
        <div className="flex-1 min-w-0 pr-3">
          <p className="text-white font-bold text-sm truncate">{booking.customer_name || 'Customer'}</p>
          <p className="text-white/30 text-xs mt-0.5 truncate">
            {experienceName(booking.experience_type)} · {fmtDate(booking.date) || '—'} · {booking.time_slot || '—'}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isWalkIn(booking) && (
            <span className="hidden sm:inline-block text-[10px] font-bold tracking-widest px-2 py-1 bg-white/10 text-white/60">WALK-IN</span>
          )}
          <span className={`text-[10px] font-bold tracking-widest px-2.5 py-1 ${STATUS_STYLES[status] || 'bg-white/5 text-white/40'}`}>
            {status.toUpperCase()}
          </span>
          {open ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-white/5 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3">
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">EMAIL</p>
              <p className="text-white/60 text-xs break-all">{booking.customer_email || '—'}</p>
            </div>
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">PHONE</p>
              <p className="text-white/60 text-xs">{booking.customer_phone || '—'}</p>
            </div>
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">RIG</p>
              <p className="text-white/60 text-xs">{rigName(booking.rig_type)}</p>
            </div>
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">PAID</p>
              <p className="text-white/60 text-xs">{booking.total_price != null ? `£${Number(booking.total_price).toFixed(2)}` : '—'}</p>
            </div>
          </div>

          {booking.referral_source && (
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">HOW THEY HEARD ABOUT US</p>
              <p className="text-white/60 text-xs">{referralLabel(booking.referral_source)}</p>
            </div>
          )}

          {booking.notes && (
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">NOTES</p>
              <p className="text-white/60 text-xs leading-relaxed">{booking.notes}</p>
            </div>
          )}

          <BookingDisclaimerPanel bookingId={booking.id} customerEmail={booking.customer_email} />

          {showEmail && <BookingEmailComposer booking={booking} onClose={() => setShowEmail(false)} />}
          {showSms && <BookingSmsComposer booking={booking} onClose={() => setShowSms(false)} />}
          {showReschedule && <SystemUpdateReschedulePanel booking={booking} onClose={() => setShowReschedule(false)} />}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setShowEmail((s) => !s); setShowSms(false); setShowReschedule(false); }}
              className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-3 py-1.5 border border-white/10 text-white/60 hover:text-white hover:border-primary/50 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" /> EMAIL CUSTOMER
            </button>
            <button
              onClick={() => { setShowSms((s) => !s); setShowEmail(false); setShowReschedule(false); }}
              className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-3 py-1.5 border border-white/10 text-white/60 hover:text-white hover:border-primary/50 transition-colors"
            >
              <MessageSquare className="w-3.5 h-3.5" /> TEXT CUSTOMER
            </button>
            <CaseSupportLink
              customerEmail={booking.customer_email}
              customerName={booking.customer_name}
              customerPhone={booking.customer_phone}
              bookingId={booking.id}
            />
            <button
              onClick={() => { setShowReschedule((s) => !s); setShowEmail(false); setShowSms(false); }}
              className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-3 py-1.5 border border-yellow-400/30 text-yellow-400/80 hover:text-yellow-400 hover:border-yellow-400/60 transition-colors"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> SYSTEM UPDATE RESCHEDULE
            </button>
          </div>

          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">STATUS</p>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => updateStatus(s)}
                  disabled={saving}
                  className={`text-[10px] font-bold tracking-widest px-3 py-1.5 border transition-colors disabled:opacity-40 ${
                    s === status ? 'border-primary text-primary bg-primary/10' : 'border-white/10 text-white/50 hover:text-white hover:border-white/25'
                  }`}
                >
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminBookings() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sort, setSort] = useState('created');
  const [view, setView] = useState('bookings');
  const queryClient = useQueryClient();

  // Deep-link support: ?tab=waitlist opens the WAITLIST tab on load.
  useEffect(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    if (tab === 'waitlist' || tab === 'walkin' || tab === 'bookings') {
      setView(tab);
    }
  }, []);

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['admin-bookings'],
    queryFn: () => base44.entities.Booking.list('-created_date', 1000),
    enabled: currentUser?.role === 'admin',
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  const applyDate = (b) => {
    if (dateFilter === 'all') return true;
    const iso = (b.date || '').slice(0, 10);
    if (!iso) return false;
    const d = new Date(iso + 'T00:00:00');
    const todayD = new Date(); todayD.setHours(0, 0, 0, 0);
    if (dateFilter === 'today') return iso === todayD.toISOString().slice(0, 10);
    if (dateFilter === 'upcoming') return d >= todayD;
    if (dateFilter === 'past') return d < todayD;
    if (dateFilter === 'thisweek') {
      const start = new Date(todayD); start.setDate(todayD.getDate() - todayD.getDay());
      const end = new Date(start); end.setDate(start.getDate() + 7);
      return d >= start && d < end;
    }
    return true;
  };

  const filtered = bookings.filter((b) => {
    if (filter !== 'all' && b.status !== filter) return false;
    if (!applyDate(b)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [b.customer_name, b.customer_email, b.experience_type, b.rig_type, b.date, b.time_slot]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  const pendingWalkIns = bookings.filter((b) => isWalkIn(b) && b.status === 'pending');
  const pendingWalkInCount = pendingWalkIns.length;

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'date_asc') return new Date((a.date || '') + 'T00:00:00') - new Date((b.date || '') + 'T00:00:00');
    if (sort === 'date_desc') return new Date((b.date || '') + 'T00:00:00') - new Date((a.date || '') + 'T00:00:00');
    return 0;
  });

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">BOOKINGS & SIM</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Manage Bookings</h1>
          <p className="text-white/30 text-sm mt-2 max-w-xl">
            Everyone who has paid for a booking session. Update status, view customer and rig details. Set up the bookable experiences and rigs under the dedicated pages.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <Link to="/admin/experiences" className="border border-white/10 hover:border-primary/50 text-white/50 hover:text-white font-bold tracking-wider px-3 py-2 transition-all">EXPERIENCES</Link>
            <Link to="/admin/rigs" className="border border-white/10 hover:border-primary/50 text-white/50 hover:text-white font-bold tracking-wider px-3 py-2 transition-all">SIM RIGS</Link>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pt-10 pb-12 max-w-5xl mx-auto">
        <div className="flex gap-1 mb-6 border-b border-white/5">
          <button onClick={() => setView('bookings')} className={`px-4 py-2.5 text-[11px] font-bold tracking-widest border-b-2 transition-colors ${view === 'bookings' ? 'border-primary text-primary' : 'border-transparent text-white/40 hover:text-white'}`}>BOOKINGS</button>
          <button onClick={() => setView('waitlist')} className={`px-4 py-2.5 text-[11px] font-bold tracking-widest border-b-2 transition-colors ${view === 'waitlist' ? 'border-primary text-primary' : 'border-transparent text-white/40 hover:text-white'}`}>WAITLIST</button>
          <button onClick={() => setView('walkin')} className={`px-4 py-2.5 text-[11px] font-bold tracking-widest border-b-2 transition-colors ${view === 'walkin' ? 'border-primary text-primary' : 'border-transparent text-white/40 hover:text-white'}`}>WALK-IN PENDING{pendingWalkInCount > 0 ? ` (${pendingWalkInCount})` : ''}</button>
        </div>
        {view === 'waitlist' ? (
          <WaitlistAdmin />
        ) : view === 'walkin' ? (
          <>
            <div className="mb-5 p-4 border border-yellow-400/20 bg-yellow-400/5">
              <p className="text-yellow-400 text-xs font-bold tracking-widest mb-1">RECONCILE WALK-IN PAYMENTS</p>
              <p className="text-white/40 text-xs">These iPad kiosk sessions haven't been confirmed. Verify the payment in Square, then mark <span className="text-white font-bold">CONFIRMED</span> — or <span className="text-white font-bold">CANCEL</span> if the customer didn't pay.</p>
            </div>
            {pendingWalkInCount === 0 ? (
              <div className="text-center py-16 border border-white/5 bg-[#111115]">
                <Calendar className="w-10 h-10 text-white/10 mx-auto mb-4" />
                <p className="text-white/30 text-sm">No pending walk-in sessions</p>
                <p className="text-white/20 text-xs mt-1">Walk-in bookings awaiting payment will appear here.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                <p className="text-white/30 text-xs font-bold tracking-widest mb-1">{pendingWalkInCount} PENDING WALK-IN{pendingWalkInCount !== 1 ? 'S' : ''}</p>
                {pendingWalkIns.map((b) => <BookingRow key={b.id} booking={b} onStatusChange={refresh} />)}
              </div>
            )}
          </>
        ) : (
          <>
        <BookingsKpiStrip bookings={bookings} />

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer, email, date…"
              className="w-full bg-[#111115] border border-white/10 text-white text-sm h-10 pl-9 pr-3 placeholder:text-white/20 focus:border-primary/60 focus:outline-none"
            />
          </div>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="bg-[#111115] border border-white/10 text-white text-sm font-bold h-10 px-3 focus:outline-none focus:border-primary/60"
          >
            <option value="all" className="bg-[#0d0d0f]">ALL DATES</option>
            <option value="today" className="bg-[#0d0d0f]">TODAY</option>
            <option value="thisweek" className="bg-[#0d0d0f]">THIS WEEK</option>
            <option value="upcoming" className="bg-[#0d0d0f]">UPCOMING</option>
            <option value="past" className="bg-[#0d0d0f]">PAST</option>
          </select>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-[#111115] border border-white/10 text-white text-sm font-bold h-10 px-3 focus:outline-none focus:border-primary/60"
          >
            <option value="all" className="bg-[#0d0d0f]">ALL STATUS</option>
            {STATUSES.map((s) => (
              <option key={s} value={s} className="bg-[#0d0d0f]">{s.toUpperCase()}</option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-[#111115] border border-white/10 text-white text-sm font-bold h-10 px-3 focus:outline-none focus:border-primary/60"
          >
            <option value="created" className="bg-[#0d0d0f]">NEWEST</option>
            <option value="date_asc" className="bg-[#0d0d0f]">SESSION DATE ↑</option>
            <option value="date_desc" className="bg-[#0d0d0f]">SESSION DATE ↓</option>
          </select>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-white/5 bg-[#111115]">
            <Calendar className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">No bookings found</p>
            <p className="text-white/20 text-xs mt-1">Paid bookings will appear here automatically.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            <p className="text-white/30 text-xs font-bold tracking-widest mb-1">{filtered.length} BOOKING{filtered.length !== 1 ? 'S' : ''}</p>
            {sorted.map((b) => <BookingRow key={b.id} booking={b} onStatusChange={refresh} />)}
          </div>
        )}
          </>
        )}
      </section>
    </div>
  );
}